import React, { useId } from 'react';
import { useTranslations } from 'next-intl';
import { BundleHostSlot } from '@/ui/components/pages/ProductDetail/types/bundle-host-slot';
import { ParsedProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedProductVariant';
import { SelectedProductVariants } from '@/ui/components/pages/ProductDetail/types/SelectedProductVariants';
import { Bundle } from '@/ui/services/BundleService';
import { BundleSlotRow } from './bundle-slot-row';

export interface BundleConfiguratorProps {
  bundle: Bundle;
  selectedVariants: SelectedProductVariants;
  onSelectVariant: (bundleProductInstanceId: string, variant: ParsedProductVariant) => void;
  hostSlot?: BundleHostSlot;
  // Known apart from the host slot, which a host without cylinder tiles does not get.
  hostProductId?: string;
}

/**
 * "In this bundle" list of a selected bundle: one row per item, with option tiles
 * for the items the visitor can configure. The host product's row goes through
 * the host's own setter, so the host pickers and the bundle stay in sync.
 */
export function BundleConfigurator({
  bundle,
  selectedVariants,
  onSelectVariant,
  hostSlot,
  hostProductId,
}: Readonly<BundleConfiguratorProps>) {
  const t = useTranslations('BundleVariants');
  const headingId = useId();

  return (
    <div data-testid="bundle-configurator">
      <h3 id={headingId} className="text-xs font-bold uppercase tracking-wider text-neutral-70">
        {t('inThisBundle')}
      </h3>
      <ul aria-labelledby={headingId}>
        {bundle.products.map((product) => {
          const instanceId = product.bundleProductInstanceId;
          const isHost = product.id === hostProductId;

          return (
            <li key={instanceId}>
              <BundleSlotRow
                product={product}
                selectedVariant={product.resolveVariant(selectedVariants)}
                onSelect={(variant) => onSelectVariant(instanceId, variant)}
                isHost={isHost}
                hostSlot={isHost && hostSlot?.productId === product.id ? hostSlot : undefined}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
